import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Search, ChevronLeft, Sparkles, Sprout, FileText, BookOpen, UserCheck, X, Mail, UserPlus, Key, Copy, Unlock, Trash2, Eye, Wand2, MapPin } from "lucide-react";
import InviteGrowerModal from "@/components/console/InviteGrowerModal";
import ManualGrowerModal from "@/components/console/ManualGrowerModal";
import GrowerProfileEditor from "@/components/console/GrowerProfileEditor";
import { StatusIcon } from "@/components/StatusIcon";

const STAGE_META = {
  registered: { label: "Registered", icon: UserCheck, color: "text-[var(--bloom-ink-muted)]" },
  reading: { label: "Reading", icon: BookOpen, color: "text-[var(--bloom-ink-muted)]" },
  ready_to_apply: { label: "Ready to apply", icon: BookOpen, color: "text-[var(--bloom-warning)]" },
  applicant: { label: "Applicant", icon: FileText, color: "text-[var(--bloom-warning)]" },
  approved: { label: "Approved", icon: Sprout, color: "text-[var(--bloom-ink)]" },
  active: { label: "Active", icon: Sparkles, color: "text-[var(--bloom-accent)]" },
};

const STAGES_FILTER = [
  { id: "", label: "All growers" },
  { id: "registered", label: "Registered" },
  { id: "reading", label: "Reading prospectus" },
  { id: "ready_to_apply", label: "Ready to apply" },
  { id: "applicant", label: "In application" },
  { id: "approved", label: "Approved" },
  { id: "active", label: "Active" },
];

export default function ConsoleGrowers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [selected, setSelected] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [showManual, setShowManual] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (stage) params.set("status", stage);
    const { data } = await api.get(`/admin/growers${params.toString() ? `?${params}` : ""}`);
    setItems(data.growers || []);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stage]);

  const openDetail = async (userId) => {
    setSelected({ loading: true });
    const { data } = await api.get(`/admin/growers/${userId}`);
    setSelected(data);
  };

  return (
    <div data-testid="console-growers">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--bloom-ink-muted)] mb-1">Heritage Bloom Club</div>
          <h1 className="font-serif text-5xl md:text-6xl leading-none">Growers</h1>
          <p className="font-serif italic text-lg text-[var(--bloom-ink-soft)] mt-3">Every soul in the orchard, from the first sign up to the activated Founding Grower.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowManual(true)} className="bloom-btn bloom-btn-outline" data-testid="manual-grower-btn">
            <Wand2 size={14} /> Add manually
          </button>
          <button onClick={() => setShowInvite(true)} className="bloom-btn bloom-btn-primary" data-testid="invite-grower-btn">
            <UserPlus size={14} /> Invite a Grower
          </button>
        </div>
      </div>

      {/* Filter / Search */}
      <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="growers-filter-bar">
        <div className="flex items-center gap-2 border border-[var(--bloom-border)] px-3 py-2 bg-white/50 flex-1 min-w-[240px]">
          <Search size={14} className="text-[var(--bloom-ink-muted)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search by name, email or farm…" className="flex-1 bg-transparent outline-none text-sm" data-testid="growers-search-input" />
          {q && <button onClick={() => { setQ(""); setTimeout(load, 0); }} className="text-[var(--bloom-ink-muted)]"><X size={14} /></button>}
        </div>
        <select value={stage} onChange={(e) => setStage(e.target.value)} className="border border-[var(--bloom-border)] px-3 py-2 bg-white/50 text-sm" data-testid="growers-stage-filter">
          {STAGES_FILTER.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <button onClick={load} className="bloom-btn bloom-btn-outline" data-testid="growers-refresh">Refresh</button>
        <div className="ml-auto text-sm text-[var(--bloom-ink-muted)]">{items.length} grower{items.length !== 1 ? "s" : ""}</div>
      </div>

      {/* Stage tiles */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="growers-stage-tiles">
        {STAGES_FILTER.slice(1).map((s) => {
          const count = items.filter((g) => g.stage === s.id).length;
          const meta = STAGE_META[s.id];
          const Icon = meta?.icon || UserCheck;
          const isActive = stage === s.id;
          return (
            <button key={s.id} onClick={() => setStage(isActive ? "" : s.id)} className={`text-left p-3 border transition ${isActive ? "border-[var(--bloom-ink)] bg-[var(--bloom-surface)]" : "border-[var(--bloom-border)] bg-white/40 hover:bg-white/70"}`} data-testid={`stage-tile-${s.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <StatusIcon name={`grower.${s.id}`} size={18} fallback={<Icon size={12} className={meta?.color} strokeWidth={1.5} />} />
                <div className="bloom-label" style={{ fontSize: "0.65rem" }}>{s.label}</div>
              </div>
              <div className="font-serif text-2xl">{count}</div>
            </button>
          );
        })}
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-16 font-serif italic text-[var(--bloom-ink-muted)]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bloom-card p-12 text-center font-serif italic text-[var(--bloom-ink-muted)]" data-testid="growers-empty">No growers match.</div>
      ) : (
        <div className="border border-[var(--bloom-border)] overflow-x-auto bg-white/40" data-testid="growers-table">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bloom-surface)] text-left">
              <tr>
                <Th>Name</Th><Th>Farm</Th><Th>Region</Th><Th>Stage</Th><Th>Reading</Th><Th>Application</Th><Th>Last seen</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((g) => {
                const meta = STAGE_META[g.stage] || STAGE_META.registered;
                const Icon = meta.icon;
                const chapters = `${g.summary?.chapters_completed || 0}/${g.summary?.chapters_total || 15}`;
                return (
                  <tr key={g.user.id} onClick={() => openDetail(g.user.id)} className="border-t border-[var(--bloom-border)] hover:bg-[var(--bloom-surface)] cursor-pointer" data-testid={`grower-row-${g.user.id}`}>
                    <Td>
                      <div className="font-serif text-base">{g.user.name}</div>
                      <div className="text-xs text-[var(--bloom-ink-muted)]">{g.user.email}</div>
                    </Td>
                    <Td>{g.user.farm_name || <em className="text-[var(--bloom-ink-muted)]"></em>}</Td>
                    <Td>{g.user.region || <em className="text-[var(--bloom-ink-muted)]"></em>}</Td>
                    <Td>
                      <span className={`inline-flex items-center gap-2 text-xs tracking-[0.1em] uppercase ${meta.color}`}>
                        <StatusIcon name={`grower.${g.stage}`} size={16} fallback={<Icon size={11} strokeWidth={1.5} />} /> {meta.label}
                      </span>
                    </Td>
                    <Td><span className="font-serif text-base">{chapters}</span><span className="text-xs text-[var(--bloom-ink-muted)] ml-1">ch</span></Td>
                    <Td>{g.application_status ? <span className="text-xs tracking-[0.1em] uppercase">{g.application_status.replace(/_/g, " ")}{g.score ? ` · ${g.score}` : ""}</span> : <em className="text-xs text-[var(--bloom-ink-muted)]"></em>}</Td>
                    <Td><span className="text-xs">{g.updated_at ? new Date(g.updated_at).toLocaleDateString() : "·"}</span></Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <GrowerDrawer
          data={selected}
          onClose={() => setSelected(null)}
          onDeleted={(deletedId) => {
            setItems((arr) => arr.filter((g) => g.user.id !== deletedId));
          }}
        />
      )}
      {showInvite && (
        <InviteGrowerModal
          onClose={() => setShowInvite(false)}
          onInvited={() => { load(); }}
        />
      )}
      {showManual && (
        <ManualGrowerModal
          onClose={() => setShowManual(false)}
          onCreated={() => { load(); }}
        />
      )}
    </div>
  );
}

function GrowerDrawer({ data, onClose, onDeleted }) {
  const [resetPw, setResetPw] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [unlockBusy, setUnlockBusy] = useState(false);
  const [unlockedNote, setUnlockedNote] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [showProfileEditor, setShowProfileEditor] = useState(false);

  if (data.loading) {
    return (
      <div className="fixed inset-0 z-50 bg-[var(--bloom-ink)]/60 backdrop-blur-sm flex items-center justify-center">
        <div className="text-white font-serif italic">Loading…</div>
      </div>
    );
  }
  const { user, summary, application, signature_status } = data;
  const alreadyUnlocked = summary.stage3_unlocked || unlockedNote;

  const doReset = async () => {
    if (!window.confirm(`Generate a new temporary password for ${user.name}? Their current password will stop working.`)) return;
    setResetBusy(true);
    try {
      const { data: r } = await api.post(`/admin/growers/${user.id}/reset-password`);
      setResetPw(r.temp_password);
    } finally {
      setResetBusy(false);
    }
  };

  const doUnlock = async () => {
    if (!window.confirm(`Unlock Stage 3 (application) for ${user.name}? This skips the prospectus and knowledge check.`)) return;
    setUnlockBusy(true);
    try {
      await api.post(`/admin/growers/${user.id}/unlock-stage3`);
      setUnlockedNote(true);
    } finally {
      setUnlockBusy(false);
    }
  };

  const doDelete = async () => {
    if (!window.confirm(`Permanently delete ${user.name}? This removes their account, progress, and any application & signatures. This cannot be undone.`)) return;
    if (!window.confirm(`Final confirmation: delete ${user.email}?`)) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/admin/growers/${user.id}`);
      onDeleted?.(user.id);
      onClose();
    } finally {
      setDeleteBusy(false);
    }
  };

  const copyPw = () => {
    navigator.clipboard.writeText(resetPw);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--bloom-ink)]/60 backdrop-blur-sm" onClick={onClose} data-testid="grower-drawer-overlay">
      <div className="absolute right-0 top-0 bottom-0 w-full md:w-[720px] bg-[var(--bloom-bg)] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-[var(--bloom-border)] bg-[var(--bloom-bg)]">
          <button onClick={onClose} className="bloom-btn bloom-btn-ghost text-xs" data-testid="grower-drawer-close">
            <ChevronLeft size={14} /> Back
          </button>
          <a href={`mailto:${user.email}`} className="bloom-btn bloom-btn-outline text-xs">
            <Mail size={12} /> Email
          </a>
        </div>

        <div className="p-8 md:p-10">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] mb-2">Grower</div>
          <h2 className="font-serif text-4xl mb-1">{user.name}</h2>
          <div className="text-sm text-[var(--bloom-ink-soft)]">{user.email}{user.farm_name && <> · <em>{user.farm_name}</em></>}{user.region && <> · {user.region}</>}</div>

          <div className="grid grid-cols-3 gap-3 mt-6">
            <Tile label="Chapters" value={`${summary.chapters_completed}/${summary.chapters_total}`} />
            <Tile label="Documents" value={`${summary.documents_read}/${summary.documents_total}`} />
            <Tile label="Read time" value={fmtTime(summary.time_seconds)} />
          </div>

          {/* Stage 2 progress */}
          <Section title="Stage 2 · Prospectus">
            <Row k="Knowledge check" v={summary.knowledge_check_passed ? `Passed (${summary.knowledge_check_score}%)` : (summary.knowledge_check_score != null ? `Attempted (${summary.knowledge_check_score}%)` : "Not attempted")} />
            <Row k="Acknowledgement" v={summary.acknowledgement_completed ? "Signed" : "·"} />
            <Row k="Stage 3 unlocked" v={summary.stage3_unlocked ? "Yes" : "No"} />
          </Section>

          {/* Application */}
          <Section title="Application">
            {application ? (
              <>
                <Row k="Status" v={application.status.replace(/_/g, " ")} />
                <Row k="Submitted" v={application.submitted_at ? new Date(application.submitted_at).toLocaleString() : "·"} />
                <Row k="Score" v={application.score ? `${application.score.score}/100 · ${application.score.tier}` : "·"} />
                <Row k="Activated" v={application.activated_at ? new Date(application.activated_at).toLocaleString() : "·"} />
                {application.review_notes && <div className="mt-3 italic font-serif text-sm text-[var(--bloom-ink-soft)]">&ldquo;{application.review_notes}&rdquo;</div>}
              </>
            ) : (
              <Empty>Hasn&rsquo;t started an application yet.</Empty>
            )}
          </Section>

          {/* Territories */}
          <TerritorySection userId={user.id} />

          {/* Signatures */}
          {signature_status && (
            <Section title={`Signatures (${signature_status.signed_count}/${signature_status.total})`}>
              {(application?.signatures || []).length === 0 ? (
                <Empty>No documents signed.</Empty>
              ) : (
                <ul className="space-y-2">
                  {application.signatures.map((s) => (
                    <li key={s.document_id || `${s.signed_at}-${s.signed_name}`} className="text-xs border-l-2 border-[var(--bloom-accent)] pl-3 py-1">
                      <div className="font-serif text-sm">{s.document_title}</div>
                      <div className="text-[var(--bloom-ink-muted)]">{s.signed_name} · {new Date(s.signed_at).toLocaleString()}</div>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          )}

          {/* Quick links */}
          <div className="mt-8 flex gap-3 flex-wrap border-t border-[var(--bloom-border)] pt-6">
            <a href={`mailto:${user.email}`} className="bloom-btn bloom-btn-outline text-xs"><Mail size={12} /> Send email</a>
            {application?.status === "active" && (
              <button
                onClick={() => setShowProfileEditor(true)}
                className="bloom-btn bloom-btn-primary text-xs"
                data-testid="open-grower-profile-editor"
                title="Edit public profile, choose territory, publish to the public site"
              >
                <Eye size={12} /> Build profile
              </button>
            )}
            <button onClick={doReset} disabled={resetBusy} className="bloom-btn bloom-btn-outline text-xs" data-testid="grower-reset-password">
              <Key size={12} /> {resetBusy ? "Resetting…" : "Reset password"}
            </button>
            {!alreadyUnlocked && (
              <button onClick={doUnlock} disabled={unlockBusy} className="bloom-btn bloom-btn-outline text-xs" data-testid="grower-unlock-stage3" title="Skip the prospectus and let this grower access Stage 3 immediately">
                <Unlock size={12} /> {unlockBusy ? "Unlocking…" : "Unlock Stage 3"}
              </button>
            )}
            {alreadyUnlocked && (
              <span className="text-xs italic font-serif text-[var(--bloom-brand)] inline-flex items-center gap-1" data-testid="grower-stage3-unlocked-badge">
                <Sparkles size={11} /> Stage 3 unlocked
              </span>
            )}
            <button
              onClick={doDelete}
              disabled={deleteBusy}
              className="bloom-btn bloom-btn-outline text-xs ml-auto"
              style={{ color: "var(--bloom-accent)", borderColor: "var(--bloom-accent)" }}
              data-testid="grower-delete"
              title="Permanently delete this grower account"
            >
              <Trash2 size={12} /> {deleteBusy ? "Deleting…" : "Delete grower"}
            </button>
          </div>
          <div className="text-xs text-[var(--bloom-ink-muted)] italic font-serif mt-3">Created {new Date(user.created_at).toLocaleDateString()}</div>

          {resetPw && (
            <div className="mt-4 border border-[var(--bloom-border)] bg-[var(--bloom-surface)] p-5" data-testid="grower-reset-password-result">
              <div className="bloom-label mb-2">New temporary password</div>
              <div className="flex items-center justify-between gap-3">
                <code className="font-mono text-base">{resetPw}</code>
                <button onClick={copyPw} className="bloom-btn bloom-btn-outline text-xs" data-testid="grower-reset-copy">
                  <Copy size={12} /> {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <p className="text-xs italic text-[var(--bloom-ink-muted)] mt-3 font-serif">
                Share with the grower so they can sign in. Their previous password no longer works.
              </p>
            </div>
          )}
        </div>
      </div>
      {showProfileEditor && application?.id && (
        <GrowerProfileEditor growerId={application.id} onClose={() => setShowProfileEditor(false)} />
      )}
    </div>
  );
}

function TerritorySection({ userId }) {
  const [areas, setAreas] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/area-map");
    setAreas(data.areas || []);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!areas) return <Section title="Territories"><Empty>Loading…</Empty></Section>;

  const mine = areas.filter((a) => a.grower_id === userId || (a.backup_grower_ids || []).includes(userId));
  const assignable = areas.filter((a) => a.grower_id !== userId);

  const assign = async (areaId) => {
    if (!areaId) return;
    setBusy(true);
    try {
      await api.patch(`/admin/area-map/${areaId}`, { grower_id: userId });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const unassign = async (a) => {
    if (!window.confirm(`Remove this grower from ${a.label}?`)) return;
    setBusy(true);
    try {
      if (a.grower_id === userId) {
        await api.patch(`/admin/area-map/${a.id}`, { grower_id: null });
      } else {
        await api.patch(`/admin/area-map/${a.id}`, { backup_grower_ids: (a.backup_grower_ids || []).filter((x) => x !== userId) });
      }
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title="Territories">
      {mine.length === 0 ? (
        <Empty>Not assigned to any territory yet.</Empty>
      ) : (
        <div className="space-y-1.5 mb-3" data-testid="grower-territories">
          {mine.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-2 text-sm border border-[var(--bloom-border)] bg-white/50 px-3 py-2" data-testid={`grower-territory-${a.id}`}>
              <span className="flex items-center gap-2 font-serif">
                <MapPin size={12} className="text-[var(--bloom-brand)]" /> {a.label}
                <span className="text-[10px] tracking-[0.15em] uppercase text-[var(--bloom-ink-muted)]">
                  {a.grower_id === userId ? "primary" : "backup"} · {a.status}
                </span>
              </span>
              <button onClick={() => unassign(a)} disabled={busy} className="text-[var(--bloom-ink-muted)] hover:text-[var(--bloom-accent)]" data-testid={`grower-territory-remove-${a.id}`}>
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
      <select value="" onChange={(e) => assign(e.target.value)} disabled={busy} className="bloom-input text-sm" data-testid="grower-territory-assign">
        <option value="">+ Assign as primary grower of…</option>
        {assignable.map((a) => (
          <option key={a.id} value={a.id}>
            {a.label} ({a.region}){a.grower_name ? ` — currently ${a.grower_name}` : ""}
          </option>
        ))}
      </select>
    </Section>
  );
}

function Tile({ label, value }) {
  return (
    <div className="border-l-2 border-[var(--bloom-accent)] pl-3">
      <div className="bloom-label" style={{ fontSize: "0.65rem" }}>{label}</div>
      <div className="font-serif text-2xl">{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-t border-[var(--bloom-border)] mt-6 pt-6">
      <h3 className="font-serif text-2xl mb-3">{title}</h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-[var(--bloom-ink-muted)]" style={{ minWidth: 140 }}>{k}</span>
      <span className="font-serif text-right" style={{ textTransform: "capitalize" }}>{String(v)}</span>
    </div>
  );
}

function Empty({ children }) {
  return <div className="text-sm italic font-serif text-[var(--bloom-ink-muted)]">{children}</div>;
}

function Th({ children }) { return <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)] font-medium">{children}</th>; }
function Td({ children }) { return <td className="px-4 py-3 align-top">{children}</td>; }

function fmtTime(seconds) {
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
